import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { MapPin, Phone, MessageCircle, ChevronRight, Clock, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EinsatzgebietePage = () => {
  const [serviceAreas, setServiceAreas] = useState([]);
  const [activeRegion, setActiveRegion] = useState("all");

  useEffect(() => {
    axios.get(`${API}/service-areas`).then(res => setServiceAreas(res.data)).catch(console.error);
  }, []);

  const hamburgAreas = serviceAreas.filter(a => a.region === "Hamburg");
  const shAreas = serviceAreas.filter(a => a.region === "Schleswig-Holstein");

  const filteredAreas = activeRegion === "all" ? serviceAreas : 
    activeRegion === "Hamburg" ? hamburgAreas : shAreas;

  return (
    <>
      <SEO 
        title="Einsatzgebiete - Hamburg & Schleswig-Holstein"
        description="Ihr mobiler Kfz-Gutachter in Hamburg und Schleswig-Holstein. Innerhalb 60 Minuten vor Ort in Hamburg, Pinneberg, Elmshorn, Wedel, Quickborn und Umgebung."
        canonical="/einsatzgebiete"
        keywords="Kfz Gutachter Hamburg, Kfz Gutachter Schleswig-Holstein, Pinneberg, Elmshorn, Sachverständiger"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img src="https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/014d598e2ff373e2844bd8b577ff45319780e085d762d506f9331e39a33aa032.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Einsatzgebiete</span>
            <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
              Kfz Gutachter in Hamburg & Schleswig-Holstein
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">
              Wir sind Ihr mobiler Kfz-Sachverständiger in der gesamten Region. Innerhalb von 60 Minuten sind wir bei Ihnen vor Ort – egal ob in Hamburg oder Schleswig-Holstein.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-sm">
                <Clock className="w-5 h-5 text-[#AA6622]" />
                <span className="font-medium">60 Min. Anfahrt</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-sm">
                <CheckCircle className="w-5 h-5 text-[#AA6622]" />
                <span className="font-medium">7 Tage die Woche</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                <Phone className="w-5 h-5" /><span>Jetzt anrufen</span>
              </a>
              <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="border-2 border-black text-black px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
                <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filter & Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[#F7F7F7] p-1 rounded-sm">
              <button onClick={() => setActiveRegion("all")} className={`px-6 py-3 rounded-sm font-bold transition-colors ${activeRegion === "all" ? "bg-[#AA6622] text-white" : "text-[#4A4A4A] hover:text-black"}`}>
                Alle Gebiete
              </button>
              <button onClick={() => setActiveRegion("Hamburg")} className={`px-6 py-3 rounded-sm font-bold transition-colors ${activeRegion === "Hamburg" ? "bg-[#AA6622] text-white" : "text-[#4A4A4A] hover:text-black"}`}>
                Hamburg
              </button>
              <button onClick={() => setActiveRegion("Schleswig-Holstein")} className={`px-6 py-3 rounded-sm font-bold transition-colors ${activeRegion === "Schleswig-Holstein" ? "bg-[#AA6622] text-white" : "text-[#4A4A4A] hover:text-black"}`}>
                Schleswig-Holstein
              </button>
            </div>
          </div>

          {/* Hamburg Section */}
          {(activeRegion === "all" || activeRegion === "Hamburg") && hamburgAreas.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit'] flex items-center">
                <MapPin className="w-6 h-6 text-[#AA6622] mr-3" />Hamburg
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {hamburgAreas.map((area) => (
                  <Link key={area.id} to={`/einsatzgebiete/${area.slug}`} className="bg-[#F7F7F7] p-4 rounded-sm hover:bg-[#AA6622] hover:text-white transition-colors group">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-[#AA6622] group-hover:text-white flex-shrink-0" />
                      <span className="font-medium">{area.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Schleswig-Holstein Section */}
          {(activeRegion === "all" || activeRegion === "Schleswig-Holstein") && shAreas.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit'] flex items-center">
                <MapPin className="w-6 h-6 text-[#AA6622] mr-3" />Schleswig-Holstein
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {shAreas.map((area) => (
                  <Link key={area.id} to={`/einsatzgebiete/${area.slug}`} className="bg-[#F7F7F7] p-4 rounded-sm hover:bg-[#AA6622] hover:text-white transition-colors group">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-[#AA6622] group-hover:text-white flex-shrink-0" />
                      <span className="font-medium">{area.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Ihr Standort nicht aufgeführt?</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Kontaktieren Sie uns – wir prüfen gerne, ob wir auch zu Ihnen kommen können. Unser Einsatzgebiet erweitert sich ständig.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <Link to="/kontakt" className="border-2 border-black text-black px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
              <span>Kontaktformular</span><ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default EinsatzgebietePage;
